/**
 * Lightweight fetch-based API client targeting the gateway public endpoints
 * via the local BFF proxy at /api/*.
 *
 * Replaces the prototype Orval client (@workspace/api-client-react) — we keep
 * the surface minimal: just the endpoints the parent guest journey uses.
 */
import { getToken, clearToken, getRefreshToken, setToken, setRefreshToken } from "./auth";

const BASE = (
  /* v8 ignore next -- BASE_URL toujours défini par Vite au chargement du module; fallback "/" inatteignable en test */
  import.meta.env.BASE_URL ?? "/"
).replace(/\/$/, "");

// Diocèse sélectionné courant (espaces économat/SEDEC). Écrit par
// DioceseScopeProvider, lu par apiFetch pour poser le header X-Diocese-Id.
let currentDioceseId: string | null = null;
export function setCurrentDioceseId(id: string | null) { currentDioceseId = id; }

// Année scolaire de travail (back-office). Écrite par AnneeScolaireProvider,
// lue par apiFetch pour poser le header X-Annee-Scolaire-Id.
let currentAnneeId: string | null = null;
export function setCurrentAnneeId(id: string | null) { currentAnneeId = id; }

export class ApiError extends Error {
  status: number;
  payload: unknown;
  constructor(status: number, message: string, payload?: unknown) {
    super(message);
    this.status = status;
    this.payload = payload;
  }
}

/** Extrait un message d'erreur lisible. `message` (détail métier du GlobalExceptionMapper) prioritaire sur `error` (code/reason). */
export function extractErrorMessage(body: unknown, statusText: string, status: number): string {
  const b = body && typeof body === "object" ? (body as Record<string, unknown>) : null;
  return (
    (b && typeof b.message === "string" && b.message) ||
    (b && typeof b.error === "string" && b.error) ||
    statusText ||
    `HTTP ${status}`
  );
}

/**
 * Endpoints publics (aucune auth requise) : recherche/facettes d'écoles,
 * motifs publics, paiement invité, catalogue de diocèses. On n'y attache
 * JAMAIS le JWT — sinon un token expiré/invalide ferait répondre 401 au
 * gateway et casserait la landing + la liste des écoles (page « vide »).
 */
function isPublicPath(path: string): boolean {
  return (
    path.startsWith("/api/etablissements/public/") ||
    path.startsWith("/api/motifs/public/") ||
    path.startsWith("/api/public/") ||
    path === "/api/dioceses" ||
    path.startsWith("/api/dioceses?") ||
    path === "/api/paiements-hors-systeme"
  );
}

const REFRESH_PATH = "/api/auth/refresh";

/**
 * Endpoints d'authentification où un 401 signifie « identifiants refusés » et
 * NON « session expirée » : on ne doit ni tenter un refresh silencieux ni purger
 * la session locale. Sinon un mauvais mot de passe (step-up, changement de mot de
 * passe) déconnecterait un utilisateur pourtant valablement connecté. `/api/auth/me`
 * n'en fait PAS partie : son 401 = access token expiré → refresh légitime.
 */
function isCredentialAuthPath(path: string): boolean {
  return (
    path === "/api/auth/login" ||
    path === "/api/auth/register" ||
    path === "/api/auth/change-password" ||
    path === "/api/auth/forgot-password" ||
    path === "/api/auth/reset-password" ||
    path.startsWith("/api/auth/otp/") ||
    path.startsWith("/api/auth/register/")
  );
}

// Une SEULE tentative de refresh partagée entre les requêtes concurrentes qui
// prennent un 401 en même temps : sans ça, N appels lanceraient N refresh, et un
// refresh token à usage unique (rotation) invaliderait les suivants.
let refreshInFlight: Promise<boolean> | null = null;

/**
 * Refresh silencieux : échange le refresh token stocké contre un nouvel access
 * token (et un nouveau refresh token si rotation). Renvoie `true` en cas de
 * succès (nouveaux tokens persistés), `false` sinon (session à purger). N'appelle
 * JAMAIS apiFetch (raw fetch) pour éviter toute récursion sur /refresh.
 */
async function tryRefresh(): Promise<boolean> {
  const rt = getRefreshToken();
  if (!rt) return false;
  if (!refreshInFlight) {
    refreshInFlight = (async () => {
      try {
        const res = await fetch(`${BASE}${REFRESH_PATH}`, {
          method: "POST",
          headers: { Accept: "application/json", "Content-Type": "application/json" },
          body: JSON.stringify({ refreshToken: rt }),
        });
        if (!res.ok) return false;
        const data = (await res.json().catch(() => null)) as
          | { accessToken?: string; refreshToken?: string }
          | null;
        if (!data?.accessToken) return false;
        setToken(data.accessToken);
        if (data.refreshToken) setRefreshToken(data.refreshToken);
        return true;
      } catch {
        return false;
      } finally {
        refreshInFlight = null;
      }
    })();
  }
  return refreshInFlight;
}

async function apiFetch<T>(path: string, init: RequestInit = {}): Promise<T> {
  return apiFetchImpl<T>(path, init, true);
}

async function apiFetchImpl<T>(
  path: string,
  init: RequestInit,
  allowRefresh: boolean,
): Promise<T> {
  const token = getToken();
  const isPublic = isPublicPath(path);
  const headers: Record<string, string> = {
    Accept: "application/json",
    ...(init.body && !(init.body instanceof FormData)
      ? { "Content-Type": "application/json" }
      : {}),
    ...((init.headers as Record<string, string>) ?? {}),
  };
  if (token && !isPublic) headers.Authorization = `Bearer ${token}`;
  if (currentDioceseId && !isPublic) headers["X-Diocese-Id"] = currentDioceseId;
  if (currentAnneeId && !isPublic) headers["X-Annee-Scolaire-Id"] = currentAnneeId;
  const res = await fetch(`${BASE}${path}`, { ...init, headers });
  const ct = res.headers.get("content-type") ?? "";
  const body = ct.includes("application/json") ? await res.json().catch(() => null) : null;
  if (!res.ok) {
    if (
      res.status === 401 &&
      token &&
      !isPublic &&
      path !== REFRESH_PATH &&
      !isCredentialAuthPath(path)
    ) {
      // Access token expiré/invalide : on tente UNE fois un refresh silencieux
      // puis on REJOUE la requête (allowRefresh=false → anti-boucle). Ce n'est
      // qu'en cas d'échec du refresh qu'on considère la session morte.
      if (allowRefresh) {
        const refreshed = await tryRefresh();
        if (refreshed) return apiFetchImpl<T>(path, init, false);
      }
      // Refresh impossible/échoué → session morte : purge (access + refresh)
      // pour ne pas casser les pages publiques au chargement suivant.
      clearToken();
    }
    const msg = extractErrorMessage(body, res.statusText, res.status);
    throw new ApiError(res.status, msg, body);
  }
  return body as T;
}

// ---------- Types ----------

export interface Diocese {
  id: string;
  slug: string;
  nom: string;
  actif?: boolean;
  nbEcoles?: number;
}

export interface EtablissementSummary {
  id: string;
  denomination: string;
  communeNom?: string | null;
  quartier?: string | null;
  typeEnseignement?: string | null;
  codeEtablissementSedec?: string | null;
  dioceseSlug?: string | null;
}

export interface EtablissementDetail extends EtablissementSummary {
  // Additional fields populated by /api/etablissements/public/{id}
  adresse?: string | null;
  telephone?: string | null;
  email?: string | null;
}

export interface EtablissementSearchResult {
  items: EtablissementSummary[];
  total: number;
  page?: number;
  size?: number;
}

export interface FacetItem {
  key: string;
  label: string;
  count: number;
}

/** Entrée de facette commune/diocèse renvoyée par l'API (FacetEntry backend). */
export interface FacetEntry {
  slug?: string;
  nom: string;
  count: number;
}

/**
 * Shape RÉEL renvoyé par /api/etablissements/public/facets (alias /api/ecoles/facets).
 * Matche EtablissementFacetsDto (school-service) :
 *   parCategorie / parTypeEnseignement / parCycle : Map<String,Long>
 *   parCommune / parDiocese : List<FacetEntry{slug,nom,count}>
 * Les clés nulles côté backend deviennent "UNKNOWN".
 */
export interface EtablissementFacets {
  parCategorie: Record<string, number>;
  parTypeEnseignement: Record<string, number>;
  parCycle?: Record<string, number>;
  parCommune: FacetEntry[];
  parDiocese: FacetEntry[];
}

export interface Motif {
  id: string;
  libelle: string;
  categorie: string;
  periode?: string | null;
  /** Forfait de l'école, tous niveaux confondus. Ne vaut PAS pour toutes les classes. */
  montantXof: number;
  /**
   * Montant réellement dû pour la classe transmise à `getMotifsForEcole`
   * (barème par classe). Absent tant qu'aucune classe n'est connue.
   * C'est LUI qu'il faut afficher et payer dès qu'on connaît l'élève.
   */
  montantApplicableXof?: number | null;
  ecoleId?: string;
  /** false = facultatif (cantine, transport…) : dû seulement si activé pour l'élève. */
  obligatoire?: boolean;
}

export interface PaiementInitResponse {
  paiementId: string;
  reference: string;
  guestToken?: string;
  redirectUrl?: string;
  devSimulateUrl?: string;
  expiresAt?: string;
}

// Doit refléter EXACTEMENT le DTO backend PaiementGuestStatusDto renvoyé par
// GET /api/paiements-public/{token} : champ `statut` (FR, pas `status`),
// et sous-objets imbriqués `ecole`/`motif`/`eleve`/`payeur`. Toute divergence
// (ex. lire `paiement.status` = undefined) bloque la page de suivi sur
// « en cours de traitement » même quand le paiement est SUCCESS.
export interface PaiementPublic {
  paiementId: string;
  reference: string;
  statut: string;
  montantXof: number;
  methode: string;
  ecole: { nom: string; code: string };
  motif: { libelle: string; categorie: string };
  eleve: { nom: string; prenoms: string; matricule?: string | null };
  payeur: { email?: string | null; telephone?: string | null };
  paidAt?: string | null;
  expiresAt?: string | null;
  recuDisponible: boolean;
  canClaim: boolean;
}

// ---------- Helpers ----------

function qs(params: Record<string, string | number | undefined | null>): string {
  const u = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) {
    if (v === undefined || v === null || v === "") continue;
    u.append(k, String(v));
  }
  const s = u.toString();
  return s ? `?${s}` : "";
}

// ---------- Endpoints ----------

export function getDioceses(): Promise<Diocese[]> {
  return apiFetch<Diocese[]>("/api/dioceses");
}

export function searchEcoles(params: {
  q?: string;
  diocese?: string;
  commune?: string;
  type?: string;
  /** Filtre catégorie (gestion_type) : DIOCESAINE / CONGREGANISTE / PARTENARIAT / CEREAO / AUTRE. */
  categorie?: string;
  /** Filtre cycle : PRESCOLAIRE / PRIMAIRE / SECONDAIRE_1 / … */
  cycle?: string;
  page?: number;
  /** Plafonné à 50 côté backend (MAX_PAGE_SIZE). */
  size?: number;
}): Promise<EtablissementSearchResult> {
  return apiFetch<EtablissementSearchResult>(
    `/api/etablissements/public/search${qs(params)}`,
  );
}

export function getEcoleFacets(params: {
  diocese?: string;
  commune?: string;
  type?: string;
  categorie?: string;
  cycle?: string;
  q?: string;
} = {}): Promise<EtablissementFacets> {
  return apiFetch<EtablissementFacets>(
    `/api/etablissements/public/facets${qs(params)}`,
  );
}

export function getEcole(id: string): Promise<EtablissementDetail> {
  return apiFetch<EtablissementDetail>(`/api/etablissements/public/${id}`);
}

/** Année scolaire courante au format "AAAA-AAAA" (bascule en août). */
export function currentSchoolYear(d: Date = new Date()): string {
  const y = d.getFullYear();
  // À partir d'août → nouvelle année scolaire (rentrée).
  return d.getMonth() >= 7 ? `${y}-${y + 1}` : `${y - 1}-${y}`;
}

export function getMotifsForEcole(ecoleId: string, classe?: string | null): Promise<Motif[]> {
  // Le catalogue public renvoie TOUJOURS les motifs de l'année scolaire ACTIVE
  // de l'école (résolue côté serveur). On n'envoie plus d'année dérivée de la
  // date du navigateur : elle divergeait de l'année active du back-office.
  //
  // `classe` (libellé) permet au serveur de joindre le barème de cette classe et
  // de renvoyer `montantApplicableXof` : les écoles tarifent PAR CLASSE, et le
  // forfait seul annonce un prix faux à la plupart des niveaux.
  const q = classe && classe.trim() ? `?classe=${encodeURIComponent(classe.trim())}` : "";
  return apiFetch<Motif[]>(
    `/api/motifs/public/etablissements/${ecoleId}/motifs${q}`,
  );
}

/**
 * Ce qu'il reste à régler pour un élève sur un motif (22/10/2026, décision du
 * PO du 21/10 : en ligne, le parent paie le reste exact d'une tranche ou du
 * motif). Lecture publique, sans compte : seulement le reste du motif et, par
 * tranche non soldée, son libellé, son échéance et son reste.
 */
export interface ResteARegler {
  motifId: string;
  /** Reste exact du motif ; 0 = déjà soldé. */
  resteXof: number;
  tranches: {
    echeanceId: string;
    libelle: string;
    dateEcheance?: string | null;
    resteXof: number;
  }[];
}

export function getResteARegler(
  ecoleId: string,
  motifId: string,
  matricule: string,
): Promise<ResteARegler> {
  return apiFetch<ResteARegler>(
    `/api/ecoles/${encodeURIComponent(ecoleId)}/motifs/${encodeURIComponent(motifId)}/reste?matricule=${encodeURIComponent(matricule)}`,
  );
}

export interface InitiatePaiementGuestBody {
  ecoleId: string;
  motifId: string;
  montantXof: number;
  /** Tranche réglée : le montant est alors son reste exact. Absent = reste du motif. */
  motifEcheanceId?: string;
  methode: string;
  payer: {
    email: string;
    telephone: string;
    prenoms: string;
    nom: string;
  };
  // Either an existing eleveId, or an inline guest eleve (manual saisie) or matricule
  eleveId?: string;
  eleveMatricule?: string;
  eleve?: {
    nom: string;
    prenoms: string;
    dateNaissance: string; // ISO YYYY-MM-DD — requis par le backend (eleveSaisie)
    classe?: string;
    anneeScolaire?: string;
  };
}

export function initiatePaiementGuest(
  body: InitiatePaiementGuestBody,
): Promise<PaiementInitResponse> {
  // Le backend (PaiementGuestInitiateRequest) attend un body PLAT :
  //   etablissementId, motifId, montantXof, methode,
  //   matriculeEnfantSedec? OU eleveSaisie{nom,prenoms,dateNaissance,classe?},
  //   payerEmail, payerTelephone, payerNom, payerPrenoms.
  // Le formulaire travaille avec un modèle ergonomique imbriqué
  // (ecoleId, payer{}, eleve{}) → on remappe ici, sinon 400 (champs plats null).
  const wire: Record<string, unknown> = {
    etablissementId: body.ecoleId,
    motifId: body.motifId,
    montantXof: body.montantXof,
    methode: body.methode,
    payerEmail: body.payer.email,
    payerTelephone: body.payer.telephone,
    payerNom: body.payer.nom,
    payerPrenoms: body.payer.prenoms,
  };
  if (body.motifEcheanceId) wire.motifEcheanceId = body.motifEcheanceId;
  if (body.eleveMatricule) {
    wire.matriculeEnfantSedec = body.eleveMatricule;
  } else if (body.eleve) {
    wire.eleveSaisie = {
      nom: body.eleve.nom,
      prenoms: body.eleve.prenoms,
      dateNaissance: body.eleve.dateNaissance,
      classe: body.eleve.classe,
    };
  }
  return apiFetch<PaiementInitResponse>("/api/paiements-public", {
    method: "POST",
    body: JSON.stringify(wire),
  });
}

export function getPaiementByToken(token: string): Promise<PaiementPublic> {
  return apiFetch<PaiementPublic>(
    `/api/paiements-public/${encodeURIComponent(token)}`,
  );
}

export function getRecuPdfUrl(token: string): string {
  return `${BASE}/api/paiements-public/${encodeURIComponent(token)}/recu.pdf`;
}

// =====================================================================
// Parent self-service (/api/me/*) — authenticated parent journey
//
// Backend contracts:
//   GET  /api/me/eleves               -> EleveDto[]            (listMyEleves)
//   GET  /api/me/paiements            -> ParentSelfPaiementPage (listMyPaiements)
//   POST /api/public/paiements/{token}/claim
//        <- PaiementGuestClaimRequest -> PaiementGuestClaimResponse (claimPaiement)
// =====================================================================

/** Élève rattaché au parent — matche EleveDto (school-service). */
export interface MyEleve {
  id: string;
  etablissementId: string;
  classeId?: string | null;
  matricule?: string | null;
  nom: string;
  prenoms: string;
  sexe?: string | null;
  dateNaissance?: string | null;
  lieuNaissance?: string | null;
  nationalite?: string | null;
  photoUrl?: string | null;
  statut?: string | null;
  creeLe?: string | null;
  modifieLe?: string | null;
  /** Dénomination de l'école de l'élève (jointure backend). */
  ecoleNom?: string | null;
  /** Libellé lisible de la classe (en plus de classeId). */
  classeLibelle?: string | null;
  /** Lien de parenté parent↔élève : PERE|MERE|TUTEUR|TUTRICE|AUTRE. */
  lien?: string | null;
  /** Libellé de l'année scolaire active de l'inscription (ex. "2025-2026"),
   *  ou null si l'élève n'est pas encore inscrit à une année. */
  anneeScolaire?: string | null;
  /** Total restant à payer pour cet enfant (XOF), agrégé côté backend.
   *  Absent/undefined si le backend ne le calcule pas → on n'affiche rien. */
  resteAPayer?: number;
}

/** Ligne de solde par motif pour un élève — matche un élément du tableau
 *  `soldes` de GET /api/me/eleves/{id}/soldes. */
export interface SoldeMotif {
  motifId: string;
  motifLibelle: string;
  duXof: number;
  payeXof: number;
  restantXof: number;
  /** true si le motif est entièrement réglé (restantXof <= 0). */
  solde: boolean;
}

/** Soldes agrégés d'un élève — matche GET /api/me/eleves/{id}/soldes. */
export interface EleveSoldes {
  totalDuXof: number;
  totalPayeXof: number;
  totalRestantXof: number;
  soldes: SoldeMotif[];
}

/** Statut d'une demande de rattachement (RattachementDemande.Statut backend, V923). */
export type MyDemandeStatut = "PENDING" | "ACCEPTED" | "REJECTED";

/** Demande de rattachement vue côté parent — matche GET /api/me/demandes. */
export interface MyDemande {
  id: string;
  eleveNom: string;
  elevePrenoms: string;
  ecoleNom?: string | null;
  statut: MyDemandeStatut | string;
  creeLe: string;
}

/** Lien de parenté possible entre le parent et l'élève. */
export type LienParente = "PERE" | "MERE" | "TUTEUR" | "TUTRICE" | "AUTRE";

/** Profil contact du parent connecté — matche GET /api/me/profil. */
export interface Profil {
  nom: string;
  prenoms: string;
  telephone: string;
  email: string;
  profession?: string | null;
  adresse?: string | null;
}

/** Corps PATCH /api/me/profil — champs partiels modifiables par le parent. */
export interface ProfilUpdate {
  telephone?: string;
  email?: string;
  profession?: string;
  adresse?: string;
}

/** Ligne paiement — matche ParentSelfPaiementRow (school-service). */
export interface MyPaiementRow {
  id: string;
  numeroPaiement: string;
  etablissementId: string;
  ecoleDenomination: string;
  eleveId?: string | null;
  eleveNom: string;
  eleveMatricule?: string | null;
  motifLabel: string;
  montant: number;
  devise: string;
  modePaiement: string;
  statut: string;
  datePaiement?: string | null;
  creeLe?: string | null;
}

/** Page paginée — matche ParentSelfPaiementPage (school-service). */
export interface MyPaiementsPage {
  items: MyPaiementRow[];
  total: number;
  page: number;
  size: number;
}

/** Enveloppe paginée générique — matche PagedResponse<T> (school-service). */
export interface Page<T> {
  items: T[];
  total: number;
  page: number;
  size: number;
}

/** Body POST /api/public/paiements/{token}/claim — matche PaiementGuestClaimRequest. */
export interface ClaimRequest {
  password: string;
  email: string;
  telephone: string;
  prenom: string;
  nom: string;
  dateNaissanceEnfant?: string;
  classeEnfant?: string;
}

/** Réponse claim — matche PaiementGuestClaimResponse. */
export interface ClaimResponse {
  userId: string;
  email: string;
  eleveLinked: boolean;
  message: string;
}

export function listMyEleves(): Promise<MyEleve[]> {
  return apiFetch<MyEleve[]>("/api/me/eleves");
}

/** Soldes (totaux + par motif) d'un élève rattaché au parent connecté. */
export function getEleveSoldes(id: string): Promise<EleveSoldes> {
  return apiFetch<EleveSoldes>(
    `/api/me/eleves/${encodeURIComponent(id)}/soldes`,
  );
}

/** Demandes de rattachement déposées par le parent connecté (statut/suivi). */
export function getMyDemandes(): Promise<MyDemande[]> {
  return apiFetch<MyDemande[]>("/api/me/demandes");
}

/** Détache un élève du compte parent (ne supprime PAS l'élève de l'école). */
export function detachEleve(id: string): Promise<void> {
  return apiFetch<void>(`/api/me/eleves/${encodeURIComponent(id)}`, {
    method: "DELETE",
  });
}

/** Met à jour le lien de parenté parent↔élève. */
export function updateEleveLien(id: string, lien: LienParente): Promise<void> {
  return apiFetch<void>(`/api/me/eleves/${encodeURIComponent(id)}/lien`, {
    method: "PATCH",
    body: JSON.stringify({ lien }),
  });
}

/** Profil contact du parent connecté. */
export function getProfil(): Promise<Profil> {
  return apiFetch<Profil>("/api/me/profil");
}

/** Met à jour le profil contact du parent (champs partiels). */
export function updateProfil(body: ProfilUpdate): Promise<Profil> {
  return apiFetch<Profil>("/api/me/profil", {
    method: "PATCH",
    body: JSON.stringify(body),
  });
}

export function listMyPaiements(params?: {
  status?: string;
  page?: number;
  size?: number;
}): Promise<MyPaiementsPage> {
  return apiFetch<MyPaiementsPage>(
    `/api/me/paiements${qs({
      status: params?.status,
      page: params?.page,
      size: params?.size,
    })}`,
  );
}

export function claimPaiement(token: string, body: ClaimRequest): Promise<ClaimResponse> {
  return apiFetch<ClaimResponse>(
    `/api/public/paiements/${encodeURIComponent(token)}/claim`,
    {
      method: "POST",
      body: JSON.stringify(body),
    },
  );
}

/**
 * Rattache un élève déjà payé au parent DÉJÀ connecté via la référence/token du
 * paiement. Contrairement à {@link claimPaiement} (endpoint guest qui CRÉE un
 * compte → 409 pour un parent existant), cet endpoint authentifié ne fait que
 * lier l'élève au compte courant — pas de mot de passe ni d'identité à fournir.
 */
export function claimElevePaiement(token: string): Promise<void> {
  return apiFetch<void>("/api/me/eleves/claim-paiement", {
    method: "POST",
    body: JSON.stringify({ token }),
  });
}

// =====================================================================
// Réconciliation parent↔élève (claim par code / demandes / validation économe)
//
// Voie B — code de rattachement émis par l'école → lien immédiat parent↔élève.
//   POST /api/me/eleves/claim { code }                      (parent, authentifié)
// Voie C — demande validée par l'économe (remplace l'ancien createEleve direct).
//   POST /api/me/demandes { ecoleId, nom, prenom, dateNaissance, … }  (parent)
// Voie D — back-office économe :
//   GET  /api/ecole/demandes                                (liste PENDING…)
//   POST /api/ecole/demandes/{id}/traiter { action, … }     (lier/créer/fusionner/rejeter)
//   POST /api/ecole/eleves/{id}/rattachement-code           (génère un code)
//   GET  /api/ecole/eleves/doublons                         (doublons suspects)
// =====================================================================

/** Demande de rattachement vue côté économe — matche RattachementDemandeDto (school-service). */
export interface EcoleDemande {
  id: string;
  etablissementId: string;
  type: string;
  statut: string;
  nom: string;
  prenoms: string;
  dateNaissance?: string | null;
  sexe?: string | null;
  lien?: string | null;
  matriculeConnu?: string | null;
  matchSuggereEleveId?: string | null;
  creeLe: string;
  // Champs de traitement (historique, arbitrage 2026-08-10) — null si PENDING.
  traiteLe?: string | null;
  motifRejet?: string | null;
  eleveResultatId?: string | null;
}

/** Statuts consultables de GET /api/ecole/demandes (arbitrage 2026-08-10). */
export type EcoleDemandeStatut = "PENDING" | "ACCEPTED" | "REJECTED" | "ALL";

/** Body POST /api/me/demandes — déposé par le parent (ne crée NI élève NI inscription). */
export interface DemandeInput {
  ecoleId: string;
  nom: string;
  prenom: string;
  dateNaissance: string;
  sexe?: string;
  lieuNaissance?: string;
  matriculeConnu?: string;
  lien?: string;
}

/** Body POST /api/ecole/demandes/{id}/traiter — action économe. */
export interface TraiterInput {
  action: "accept_link" | "accept_new" | "reject" | "merge";
  targetEleveId?: string;
  sourceEleveId?: string;
  motif?: string;
  forcerHomonyme?: boolean;
}

/** Ligne « doublon suspect » — matche EleveDoublonDto (school-service). */
export interface EleveDoublon {
  nom: string;
  prenoms: string;
  count: number;
  ids: string[];
  matricules: string[];
}

/** Voie B : revendique un élève existant via le code émis par l'école. */
export function claimEleveByCode(code: string): Promise<void> {
  return apiFetch<void>("/api/me/eleves/claim", {
    method: "POST",
    body: JSON.stringify({ code }),
  });
}

/** Voie C : dépose une demande de rattachement (validation économe ultérieure). */
export function createDemande(body: DemandeInput): Promise<{ statut: string }> {
  return apiFetch<{ statut: string }>("/api/me/demandes", {
    method: "POST",
    body: JSON.stringify(body),
  });
}

/**
 * Voie D : liste des demandes de l'école (fallback [] si endpoint indisponible).
 * `statut` (arbitrage 2026-08-10) : PENDING par défaut, ACCEPTED/REJECTED/ALL
 * pour relire l'historique des demandes traitées.
 */
export function listEcoleDemandes(statut: EcoleDemandeStatut = "PENDING"): Promise<EcoleDemande[]> {
  const qs = statut === "PENDING" ? "" : `?statut=${statut}`;
  return apiFetchOrEmpty<EcoleDemande[]>(`/api/ecole/demandes${qs}`, []);
}

/** Voie D : traite une demande (lier / créer / fusionner / rejeter). */
export function traiterDemande(id: string, body: TraiterInput): Promise<EcoleDemande> {
  return apiFetch<EcoleDemande>(
    `/api/ecole/demandes/${encodeURIComponent(id)}/traiter`,
    { method: "POST", body: JSON.stringify(body) },
  );
}

/**
 * Voie B (économe) : génère un code de rattachement pour un élève.
 * Renvoie le code (saisie manuelle) ET un jeton QR opaque (à encoder dans un QR
 * scannable par l'app mobile — non réutilisable hors app).
 */
export function genererCodeRattachement(
  eleveId: string,
): Promise<{ code: string; qrToken?: string }> {
  return apiFetch<{ code: string; qrToken?: string }>(
    `/api/ecole/eleves/${encodeURIComponent(eleveId)}/rattachement-code`,
    { method: "POST" },
  );
}

/** Doublons suspects (même identité normalisée dans l'école). */
export function listDoublons(): Promise<EleveDoublon[]> {
  return apiFetchOrEmpty<EleveDoublon[]>("/api/ecole/eleves/doublons", []);
}

// ---------- Auth ----------
//
// IMPORTANT — l'auth-service renvoie les champs À PLAT et avec les noms
// firstName/lastName/roles[] (PAS d'objet `user` imbriqué, PAS de `role`
// singulier). /api/auth/login : { accessToken, roles[], firstName, ... }.
// /api/auth/me : { id, firstName, lastName, roles[], ... }.
// On normalise vers AuthMe (prenoms/nom/roles) pour tout le front.

export interface AuthMe {
  id: string;
  email?: string;
  phone?: string;
  prenoms?: string;
  nom?: string;
  /** Rôles canoniques (minuscule) renvoyés par l'auth-service, ex ["sedec_admin","user"]. */
  roles: string[];
}

/** Réponse brute (plate) de l'auth-service login/register. */
interface RawAuthResponse {
  accessToken: string;
  /** Token de rafraîchissement (7 j) — à persister pour le refresh silencieux. */
  refreshToken?: string;
  userId?: string;
  id?: string;
  email?: string;
  phone?: string;
  firstName?: string;
  lastName?: string;
  prenoms?: string;
  nom?: string;
  roles?: string[];
}

export interface AuthLoginResponse {
  accessToken: string;
  /** Token de rafraîchissement à stocker (setRefreshToken) pour le refresh silencieux. */
  refreshToken?: string;
  /** Utilisateur normalisé (mappé depuis la réponse plate auth-service). */
  user: AuthMe;
}

function normalizeAuthUser(raw: RawAuthResponse): AuthMe {
  return {
    id: raw.id ?? raw.userId ?? "",
    email: raw.email,
    phone: raw.phone,
    prenoms: raw.prenoms ?? raw.firstName,
    nom: raw.nom ?? raw.lastName,
    roles: Array.isArray(raw.roles) ? raw.roles : [],
  };
}

/** Groupes de rôles → espace d'accueil. Accepte les libellés UPPERCASE proto. */
const ROLE_HOME: Array<{ roles: string[]; home: string }> = [
  { roles: ["sedec_admin", "school_admin", "super_admin", "admin", "diocese_sedec_admin", "diocese_super_admin"], home: "/sedec" },
  { roles: ["econome_diocesain", "auditeur_diocesain", "diocese_economat", "diocese_auditeur"], home: "/economat" },
  { roles: ["econome", "econome_etab", "caisse", "ecole_econome", "ecole_caisse"], home: "/ecole" },
  { roles: ["parent", "user"], home: "/dashboard" },
];

/**
 * Espace d'accueil pour un ensemble de rôles. Priorité décroissante
 * (sedec > economat > ecole > parent) — un sedec_admin qui a aussi "user"
 * va sur /sedec, pas /dashboard. Défaut : /dashboard.
 */
export function homeForRoles(roles: string[] | undefined): string {
  const set = new Set((roles ?? []).map((r) => r.toLowerCase()));
  for (const { roles: group, home } of ROLE_HOME) {
    if (group.some((r) => set.has(r))) return home;
  }
  return "/dashboard";
}

export function authLogin(body: {
  email: string;
  password: string;
}): Promise<AuthLoginResponse> {
  return apiFetch<RawAuthResponse>("/api/auth/login", {
    method: "POST",
    body: JSON.stringify(body),
  }).then((raw) => ({
    accessToken: raw.accessToken,
    refreshToken: raw.refreshToken,
    user: normalizeAuthUser(raw),
  }));
}

/**
 * Étape 1 de l'inscription parent : demande l'envoi d'un code de vérification
 * (email + SMS) via auth-service. À appeler avant {@link authRegisterParent}.
 */
export function requestRegistrationOtp(body: {
  email: string;
  phone: string;
}): Promise<{ message: string }> {
  return apiFetch<{ message: string }>("/api/auth/register/request-otp", {
    method: "POST",
    body: JSON.stringify(body),
  });
}

/**
 * Étape 2 : crée le compte parent avec le code OTP reçu. Le front utilise les
 * libellés FR (prenoms/nom) ; auth-service attend firstName/lastName — on traduit.
 */
export function authRegisterParent(body: {
  nom: string;
  prenoms: string;
  email: string;
  phone: string;
  password: string;
  confirmPassword: string;
  otpCode: string;
}): Promise<AuthLoginResponse> {
  const { nom, prenoms, ...rest } = body;
  return apiFetch<RawAuthResponse>("/api/auth/register", {
    method: "POST",
    body: JSON.stringify({ ...rest, firstName: prenoms, lastName: nom }),
  }).then((raw) => ({
    accessToken: raw.accessToken,
    refreshToken: raw.refreshToken,
    user: normalizeAuthUser(raw),
  }));
}

export function authMe(): Promise<AuthMe> {
  return apiFetch<RawAuthResponse>("/api/auth/me").then(normalizeAuthUser);
}

/**
 * Déconnexion serveur : révoque la/les session(s) de l'utilisateur (et le
 * refresh token). Appelée en best-effort par useCurrentUser().logout — l'appelant
 * ignore l'échec (réseau/hors-ligne) et purge quand même le token local.
 */
export function authLogout(): Promise<void> {
  return apiFetch<void>("/api/auth/logout", { method: "POST" });
}

/**
 * Change le mot de passe de l'utilisateur courant (self-service). L'auth-service
 * vérifie `currentPassword` avant d'appliquer `newPassword`.
 */
export function changeMyPassword(currentPassword: string, newPassword: string): Promise<void> {
  return apiFetch<void>("/api/auth/change-password", {
    method: "POST",
    body: JSON.stringify({ currentPassword, newPassword }),
  });
}

// =====================================================================
// Back-office endpoints (école / économat / sedec)
//
// All scoped endpoints are implemented by the school-service:
//   EcoleResource              → /api/ecole/*
//   EconomatResource           → /api/economat/*
//   SedecBackofficeResource    → /api/sedec/users, /api/sedec/ecoles, /api/sedec/audit
//
// The apiFetchOrEmpty helper catches transient errors (404/501/502) and
// returns a fallback, which lets pages render a graceful fallback state
// instead of crashing — preserved for resilience, not because endpoints
// are missing.
// =====================================================================

async function apiFetchOrEmpty<T>(path: string, fallback: T, init: RequestInit = {}): Promise<T> {
  try {
    return await apiFetch<T>(path, init);
  } catch (err) {
    if (err instanceof ApiError && (err.status === 404 || err.status === 501 || err.status === 502)) {
      return fallback;
    }
    throw err;
  }
}

// ---------- Shared back-office types ----------

export type PaiementStatus =
  | "SUCCESS"
  | "PROCESSING"
  | "PENDING"
  | "FAILED"
  | "CANCELLED"
  | "REFUNDED";

export interface BackofficePaiement {
  id: string;
  reference: string;
  ecoleId: string;
  ecoleDenomination: string;
  eleveId?: string;
  eleveNom: string;
  eleveMatricule?: string;
  motifId: string;
  motifLibelle: string;
  montantXof: number;
  methode: string;
  driver?: string;
  status: PaiementStatus | string;
  createdAt: string;
  paidAt?: string | null;
}

export interface BreakdownRow {
  key: string;
  label: string;
  count: number;
  totalXof: number;
}

export interface AuditEntry {
  id: string;
  createdAt: string;
  actorEmail?: string | null;
  actorRole: string;
  action: string;
  targetType: string;
  targetId?: string | null;
  ip?: string | null;
}

// ---------- École ----------

export interface EcoleDashboard {
  ecoleId: string;
  ecoleDenomination: string;
  anneeScolaire: string;
  totalEncaisseXof: number;
  nbPaiementsSuccess: number;
  nbPaiementsPending: number;
  nbEleves: number;
  nbMotifsActifs: number;
  parMotif: BreakdownRow[];
  parMethode: BreakdownRow[];
  recentPaiements: BackofficePaiement[];
}

export interface EcoleMotif {
  id: string;
  code: string;
  libelle: string;
  categorie: string;
  periode?: string | null;
  anneeScolaire: string;
  /** Forfait HÉRITÉ — facultatif depuis le 28/07/2026, les barèmes par classe font foi. */
  montantXof: number | null;
  montantMinXof?: number | null;
  paiementPartielAutorise: boolean;
  actif: boolean;
  /** Compte produit SYSCOHADA (7061, 7062…) — imputation comptable des encaissements. */
  compteSyscohada?: string | null;
  /** false = facultatif, activé élève par élève (fiche élève) ; true/absent = dû par tous. */
  obligatoire?: boolean;
}

/** Corps POST /api/ecole/motifs — matche EcoleMotifInput (school-service) :
 *  le motif dit QUOI (libellé, catégorie, compte, période) ; l'argent vit
 *  dans les barèmes par classe. `anneeScolaire` optionnel → année courante. */
export interface EcoleMotifCreate {
  libelle: string;
  /** Forfait hérité, facultatif — l'écran de gestion ne l'envoie plus. */
  montant?: number;
  frequence?: string; // T1|T2|T3|ANNUEL|MENSUEL|INSCRIPTION
  categorie?: string;
  code?: string;
  anneeScolaire?: string;
  compteSyscohada?: string;
  /** false = facultatif (au choix par élève) ; absent = obligatoire. */
  obligatoire?: boolean;
}

/* ------------------------------------------------------------------ *
 * Motifs FACULTATIFS par élève (lot C — 29/07/2026) : cantine,
 * transport… activés élève par élève depuis la fiche élève.
 * ------------------------------------------------------------------ */

export interface EleveMotifFacultatif {
  motifId: string;
  code: string;
  libelle: string;
  /** Activation courante pour l'élève. */
  active: boolean;
  duXof: number;
  payeXof: number;
  /** Paiement validé existant → indésactivable tant qu'il n'est pas annulé. */
  verrouille: boolean;
}

export function listEleveMotifsFacultatifs(eleveId: string): Promise<EleveMotifFacultatif[]> {
  return apiFetchOrEmpty<EleveMotifFacultatif[]>(
    `/api/ecole/eleves/${encodeURIComponent(eleveId)}/motifs-facultatifs`, []);
}

/** 409 si désactivation d'un motif déjà payé ; renvoie la liste à jour. */
export function setEleveMotifFacultatif(
  eleveId: string, motifId: string, actif: boolean,
): Promise<EleveMotifFacultatif[]> {
  return apiFetch<EleveMotifFacultatif[]>(
    `/api/ecole/eleves/${encodeURIComponent(eleveId)}/motifs-facultatifs/${encodeURIComponent(motifId)}`,
    { method: "PATCH", body: JSON.stringify({ actif }) });
}

/* ------------------------------------------------------------------ *
 * Barèmes par classe et échéanciers — la VRAIE grille tarifaire.
 * Les écoles fixent un montant par classe (CP 260 000, CM 317 000…) ;
 * le forfait du motif ne peut en représenter qu'un seul.
 * ------------------------------------------------------------------ */

export interface MotifBareme {
  id: string;
  motifId: string;
  classeId: string | null;
  anneeScolaireId: string;
  montant: number;
  devise: string;
}

export interface MotifBaremeCreate {
  motifId: string;
  classeId: string;
  anneeScolaireId: string;
  montant: number;
  devise?: string;
}

export interface MotifEcheance {
  id: string;
  motifId: string;
  motifBaremeId: string | null;
  libelle: string;
  montant: number;
  dateEcheance: string;
  ordre: number;
}

/**
 * Une tranche d'un échéancier complet (PUT /api/ecole/baremes/{id}/echeancier).
 * Audit SCOM-03 du 21/10/2026 : l'ajout tranche par tranche (POST
 * /api/motif-echeances) tombait en 500, la base exigeant que la somme des
 * tranches égale le tarif ; seul le remplacement de TOUT l'échéancier passe.
 */
export interface TrancheEcheancier {
  libelle: string;
  montant: number;
  dateEcheance: string;
}

/** Classes de MON école (le périmètre vient du JWT ; l'année cible le millésime). */
export interface ClasseLite {
  id: string;
  libelle: string;
  anneeScolaireId: string;
  /** Rattachement pédagogique — la grille tarifaire saisit PAR NIVEAU. */
  niveauId: string | null;
}

/**
 * Niveaux applicables à mon école (globaux + propres), triés par ordre
 * pédagogique. Les fiches SEDEC tarifent par niveau (CM1 = un montant) ;
 * la grille regroupe donc les classes sous leur niveau.
 */
export interface NiveauLite {
  id: string;
  code: string;
  libelle: string;
  cycle: string | null;
  ordre: number;
}

export function listNiveauxEcole(): Promise<NiveauLite[]> {
  return apiFetchOrEmpty<NiveauLite[]>("/api/ecole/niveaux", []);
}

export function listClasses(anneeScolaireId?: string): Promise<ClasseLite[]> {
  const qs = anneeScolaireId ? `?anneeScolaireId=${encodeURIComponent(anneeScolaireId)}` : "";
  return apiFetchOrEmpty<ClasseLite[]>(`/api/classes${qs}`, []);
}

/* --------------------------------------------------------------- *
 * Gestion des classes — page « Classes » (créer, renommer,
 * supprimer si vide). L'école vient du JWT, l'année du header
 * X-Annee-Scolaire-Id posé par apiFetch.
 * --------------------------------------------------------------- */

export interface EcoleClasseCreate {
  niveauId: string;
  libelle: string;
  effectifMax?: number;
}

export function createClasseEcole(data: EcoleClasseCreate): Promise<ClasseLite> {
  return apiFetch<ClasseLite>("/api/ecole/classes", {
    method: "POST",
    body: JSON.stringify(data),
  });
}

/** 409 si une autre classe de l'année porte déjà ce libellé. */
export function renameClasseEcole(classeId: string, libelle: string): Promise<ClasseLite> {
  return apiFetch<ClasseLite>(`/api/ecole/classes/${encodeURIComponent(classeId)}`, {
    method: "PATCH",
    body: JSON.stringify({ libelle }),
  });
}

/** 409 si des élèves y sont rattachés — on ne supprime qu'une classe vide. */
export function deleteClasseEcole(classeId: string): Promise<void> {
  return apiFetch<void>(`/api/ecole/classes/${encodeURIComponent(classeId)}`, {
    method: "DELETE",
  });
}

export function listMotifBaremes(params: {
  motifId?: string;
  etablissementId?: string;
  anneeScolaireId?: string;
  classeId?: string;
}): Promise<MotifBareme[]> {
  const q = new URLSearchParams();
  Object.entries(params).forEach(([k, v]) => v && q.set(k, v));
  return apiFetchOrEmpty<MotifBareme[]>(`/api/motif-baremes?${q.toString()}`, []);
}

export function createMotifBareme(data: MotifBaremeCreate): Promise<MotifBareme> {
  return apiFetch<MotifBareme>("/api/motif-baremes", {
    method: "POST",
    body: JSON.stringify({ devise: "XOF", ...data }),
  });
}

export function updateMotifBareme(id: string, data: MotifBaremeCreate): Promise<MotifBareme> {
  return apiFetch<MotifBareme>(`/api/motif-baremes/${id}`, {
    method: "PATCH",
    body: JSON.stringify({ devise: "XOF", ...data }),
  });
}

export function deleteMotifBareme(id: string): Promise<void> {
  return apiFetch<void>(`/api/motif-baremes/${id}`, { method: "DELETE" });
}

export function listMotifEcheances(params: { motifId?: string; motifBaremeId?: string }): Promise<MotifEcheance[]> {
  const q = new URLSearchParams();
  Object.entries(params).forEach(([k, v]) => v && q.set(k, v));
  return apiFetchOrEmpty<MotifEcheance[]>(`/api/motif-echeances?${q.toString()}`, []);
}

/**
 * Remplace TOUT l'échéancier d'un barème (motif × classe), en une transaction
 * côté serveur. Somme des tranches = tarif de la classe ; liste vide = paiement
 * en une fois. Même geste que CATHO SCHOOL PRO (Frais).
 */
export function remplacerEcheancier(baremeId: string, tranches: TrancheEcheancier[]): Promise<MotifEcheance[]> {
  return apiFetch<MotifEcheance[]>(`/api/ecole/baremes/${encodeURIComponent(baremeId)}/echeancier`, {
    method: "PUT",
    body: JSON.stringify(tranches),
  });
}

export interface EcoleEleve {
  id: string;
  nom: string;
  prenoms: string;
  classe?: string | null;
  anneeScolaire: string;
  matriculeInterne: string;
  matriculeSedecOfficiel?: string | null;
  sexe?: "M" | "F" | null;
  /** Depuis le 21/10/2026, en minuscules : inscrit, preinscrit, suspendu, sorti
   *  ou transfere (inscription de l'année fermée). Absent d'un serveur plus ancien. */
  statut?: string | null;
}

/** Règle unique de l'effectif (21/10/2026) : seuls les élèves inscrits comptent,
 *  comme au tableau de bord et dans CATHO SCHOOL PRO. Statut absent (serveur
 *  plus ancien) : compté, comme avant. */
export function compteDansLEffectif(e: Pick<EcoleEleve, "statut">): boolean {
  return !e.statut || e.statut.toLowerCase() === "inscrit";
}

/** Élève parti (sorti ou transféré) : plus proposé à l'encaissement (21/10/2026). */
export function estParti(e: Pick<EcoleEleve, "statut">): boolean {
  const s = (e.statut ?? "").toLowerCase();
  return s === "sorti" || s === "transfere";
}

/** Corps POST /api/ecole/eleves — matche EcoleEleveCreateInput (school-service) :
 *  `prenom` (singulier) + `dateNaissance` (ISO, REQUISE), pas `prenoms`/`anneeScolaire`. */
export interface EcoleEleveCreate {
  nom: string;
  prenom: string;
  dateNaissance: string; // ISO AAAA-MM-JJ — @NotNull côté backend
  classe?: string;
  sexe?: "M" | "F";
  lieuNaissance?: string;
  parentNom?: string;
  parentTelephone?: string;
  parentEmail?: string;
}

export interface EcoleEleveSolde {
  motifId: string;
  motifCode: string;
  motifLibelle: string;
  duXof: number;
  payeXof: number;
  restantXof: number;
}

export interface EcoleEleveDetail {
  eleve: EcoleEleve;
  soldes: EcoleEleveSolde[];
  paiements: BackofficePaiement[];
  totalDuXof: number;
  totalPayeXof: number;
  totalRestantXof: number;
}

export interface EcoleManualPaiementCreate {
  eleveId: string;
  motifId: string;
  montantXof: number;
  methode: string;
  reference?: string | null;
  notes?: string | null;
  /**
   * Émettre le reçu dès l'enregistrement, comme School Pro (SCOM-06,
   * 22/10/2026). Le serveur répond alors `{ paiement, recu }`.
   */
  genererRecu?: boolean;
}

export function getEcoleDashboard(): Promise<EcoleDashboard | null> {
  return apiFetchOrEmpty<EcoleDashboard | null>("/api/ecole/dashboard", null);
}

/**
 * Liste des motifs de l'école. Par défaut → actifs uniquement (utilisé par les
 * dropdowns de SAISIE). `includeInactifs=true` → tous (actifs + inactifs) : réservé
 * à l'écran de GESTION des motifs, pour pouvoir réactiver un motif désactivé.
 */
export function listEcoleMotifs(opts?: { includeInactifs?: boolean }): Promise<EcoleMotif[]> {
  const path = `/api/ecole/motifs${opts?.includeInactifs ? "?includeInactifs=true" : ""}`;
  return apiFetchOrEmpty<EcoleMotif[]>(path, []);
}
export function createEcoleMotif(data: EcoleMotifCreate): Promise<EcoleMotif> {
  return apiFetch<EcoleMotif>("/api/ecole/motifs", {
    method: "POST",
    body: JSON.stringify(data),
  });
}
/**
 * Suppression d'un motif JAMAIS payé (lot E) — 409 avec message métier
 * (« désactivez-le… ») dès qu'un paiement le référence.
 */
export function deleteEcoleMotif(motifId: string): Promise<void> {
  return apiFetch<void>(`/api/ecole/motifs/${encodeURIComponent(motifId)}`, {
    method: "DELETE",
  });
}
export function updateEcoleMotif(
  motifId: string,
  // PATCH backend : { libelle?, montant?, frequence?, categorie?, actif? }. On
  // accepte les clés de EcoleMotif (actif…) ET de EcoleMotifCreate (montant/frequence).
  data: Partial<EcoleMotif> & Partial<EcoleMotifCreate>,
): Promise<EcoleMotif> {
  return apiFetch<EcoleMotif>(`/api/ecole/motifs/${encodeURIComponent(motifId)}`, {
    method: "PATCH",
    body: JSON.stringify(data),
  });
}

export function listEcoleEleves(): Promise<EcoleEleve[]> {
  return apiFetchOrEmpty<EcoleEleve[]>("/api/ecole/eleves", []);
}
export function createEcoleEleve(data: EcoleEleveCreate): Promise<EcoleEleve> {
  return apiFetch<EcoleEleve>("/api/ecole/eleves", {
    method: "POST",
    body: JSON.stringify(data),
  });
}
export function getEcoleEleveDetail(eleveId: string): Promise<EcoleEleveDetail> {
  return apiFetch<EcoleEleveDetail>(`/api/ecole/eleves/${encodeURIComponent(eleveId)}`);
}

/** Corps PATCH /api/ecole/eleves/{id} — champs nullables (mise à jour partielle). */
export interface EcoleEleveUpdate {
  nom?: string;
  prenom?: string;
  dateNaissance?: string; // ISO AAAA-MM-JJ
  sexe?: "M" | "F";
  matricule?: string;
  /** Id (ou libellé) d'une classe de l'école — résolution STRICTE côté backend (400 sinon). */
  classe?: string;
}
export function updateEcoleEleve(eleveId: string, data: EcoleEleveUpdate): Promise<EcoleEleve> {
  return apiFetch<EcoleEleve>(`/api/ecole/eleves/${encodeURIComponent(eleveId)}`, {
    method: "PATCH",
    body: JSON.stringify(data),
  });
}

export function listEcolePaiements(params?: { status?: string }): Promise<BackofficePaiement[]> {
  return apiFetchOrEmpty<BackofficePaiement[]>(`/api/ecole/paiements${qs({ status: params?.status })}`, []);
}
export async function createEcoleManualPaiement(data: EcoleManualPaiementCreate): Promise<BackofficePaiement> {
  const r = await apiFetch<BackofficePaiement | { paiement: BackofficePaiement; recu?: unknown }>(
    "/api/ecole/paiements/manual",
    { method: "POST", body: JSON.stringify(data) },
  );
  // Avec genererRecu, le serveur enveloppe le paiement : { paiement, recu }.
  return "paiement" in r ? r.paiement : r;
}

// ---------- Vue unifiée des paiements (réconciliation) ----------

/** Origine de la ligne : grand-livre (paiement abouti) vs entonnoir invité (en cours/échec). */
export type OrigineLigneUnifiee = "LEDGER" | "FUNNEL";

/** Source du paiement unifié. */
export type SourcePaiementUnifie = "INTERNE" | "MANUEL" | "INVITE" | "HORS_SYSTEME";

/** Statut consolidé d'un paiement unifié (grand-livre + entonnoir invité). */
export type StatutUnifie =
  | "VALIDE"
  | "INITIE"
  | "EN_COURS"
  | "ECHEC"
  | "EXPIRE"
  | "ANNULE"
  | "REJETE"
  | "REMBOURSE"
  | "EN_ATTENTE";

/**
 * Ligne de la vue unifiée des paiements — matche le DTO backend renvoyé par
 * GET /api/paiements/unifies. Mélange grand-livre (origineLigne=LEDGER) et
 * paiements invité « en cours/échoués » pas encore aboutis (origineLigne=FUNNEL).
 */
export interface PaiementUnifie {
  id: string;
  origineLigne: OrigineLigneUnifiee;
  source: SourcePaiementUnifie | string;
  statutUnifie: StatutUnifie | string;
  etablissementId: string;
  eleveId?: string | null;
  motifId?: string | null;
  anneeScolaireId?: string | null;
  montant: number;
  devise: string;
  mode?: string | null;
  reference?: string | null;
  numeroPaiement?: string | null;
  ecritureId?: string | null;
  datePaiement?: string | null;
  paiementId?: string | null;
  guestToken?: string | null;
  jekoProviderPaymentId?: string | null;
  payerNom?: string | null;
  payerPrenoms?: string | null;
  payerTelephone?: string | null;
  payerEmail?: string | null;
  parentUserId?: string | null;
  claimedAt?: string | null;
  paidAt?: string | null;
  creeLe?: string | null;
}

/**
 * Vue unifiée des paiements d'un établissement (réconciliation grand-livre +
 * entonnoir invité). `statut`/`source` filtrent côté serveur ; `limit`/`offset`
 * paginent.
 */
export function listPaiementsUnifies(
  etablissementId: string,
  opts?: { statut?: string; source?: string; limit?: number; offset?: number },
): Promise<PaiementUnifie[]> {
  return apiFetchOrEmpty<PaiementUnifie[]>(
    `/api/paiements/unifies${qs({
      etablissementId,
      statut: opts?.statut,
      source: opts?.source,
      limit: opts?.limit,
      offset: opts?.offset,
    })}`,
    [],
  );
}

// ---------- Économat ----------

export interface EconomatTopEcole {
  ecoleId: string;
  denomination: string;
  count: number;
  totalXof: number;
}

export interface EconomatDashboard {
  dioceseId: string;
  dioceseNom: string;
  totalEncaisseXof: number;
  nbPaiementsSuccess: number;
  nbPaiementsPending: number;
  nbEcolesActives: number;
  nbEcolesTotal: number;
  topEcoles: EconomatTopEcole[];
  parMethode: BreakdownRow[];
  recentPaiements: BackofficePaiement[];
}

export interface EconomatEcoleListItem {
  id: string;
  denomination: string;
  communeNom?: string | null;
  typeEnseignement?: string | null;
  actif: boolean;
  nbEleves: number;
  nbPaiements: number;
  totalEncaisseXof: number;
}

export interface EconomatEcoleDetail {
  id: string;
  denomination: string;
  communeNom?: string | null;
  typeEnseignement?: string | null;
  actif: boolean;
  totalEncaisseXof: number;
  nbPaiementsSuccess: number;
  nbPaiementsPending: number;
  nbEleves: number;
  nbMotifsActifs: number;
  parMotif: BreakdownRow[];
  parMethode: BreakdownRow[];
  recentPaiements: BackofficePaiement[];
}

export function getEconomatDashboard(): Promise<EconomatDashboard | null> {
  return apiFetchOrEmpty<EconomatDashboard | null>("/api/economat/dashboard", null);
}
export function listEconomatEcoles(params?: { page?: number; size?: number; q?: string }): Promise<Page<EconomatEcoleListItem>> {
  return apiFetch<Page<EconomatEcoleListItem>>(`/api/economat/ecoles${qs({
    page: params?.page ?? 0, size: params?.size ?? 20, q: params?.q,
  })}`);
}
export function getEconomatEcoleDetail(ecoleId: string): Promise<EconomatEcoleDetail> {
  return apiFetch<EconomatEcoleDetail>(`/api/economat/ecoles/${encodeURIComponent(ecoleId)}`);
}
export function listEconomatPaiements(params?: { status?: string; page?: number; size?: number }): Promise<Page<BackofficePaiement>> {
  return apiFetch<Page<BackofficePaiement>>(`/api/economat/paiements${qs({
    status: params?.status, page: params?.page ?? 0, size: params?.size ?? 20,
  })}`);
}
export function listEconomatAudit(params?: {
  page?: number; size?: number; q?: string; from?: string; to?: string;
}): Promise<Page<AuditEntry>> {
  return apiFetch<Page<AuditEntry>>(`/api/economat/audit${qs({
    page: params?.page ?? 0, size: params?.size ?? 20, q: params?.q, from: params?.from, to: params?.to,
  })}`);
}

// ---------- SEDEC ----------

export interface SedecUser {
  id: string;
  email: string;
  prenoms: string;
  nom: string;
  phone?: string | null;
  role: string;
  ecoleId?: string | null;
  ecoleDenomination?: string | null;
  actif: boolean;
  lastLoginAt?: string | null;
}

export interface SedecUserCreate {
  role: string;
  email: string;
  password: string;
  nom: string;
  prenoms: string;
  phone?: string;
  ecoleId?: string;
  dioceseIds?: string[];
}

export interface SedecUserPatch {
  actif?: boolean;
  role?: string;
  newPassword?: string;
  nom?: string;
  prenoms?: string;
}

export interface SedecEcole {
  id: string;
  denomination: string;
  communeNom?: string | null;
  typeEnseignement?: string | null;
  codeEtablissementSedec?: string | null;
  actif: boolean;
  nbUsers: number;
  onboardedAt?: string | null;
}

export function listSedecUsers(params: {
  page: number; size: number; search?: string; role?: string; ecoleId?: string; active?: boolean;
}): Promise<Page<SedecUser>> {
  return apiFetch<Page<SedecUser>>(
    `/api/sedec/users${qs({
      page: params.page, size: params.size, search: params.search,
      role: params.role, ecoleId: params.ecoleId,
      active: params.active === undefined ? undefined : String(params.active),
    })}`,
  );
}
export function createSedecUser(data: SedecUserCreate): Promise<SedecUser> {
  return apiFetch<SedecUser>("/api/sedec/users", {
    method: "POST",
    body: JSON.stringify(data),
  });
}
export function updateSedecUser(userId: string, data: SedecUserPatch): Promise<SedecUser> {
  // Sous-chemin /edit : le PATCH du chemin nu /users/{id} n'est pas routé
  // (cf. SedecBackofficeResource.updateUser).
  return apiFetch<SedecUser>(`/api/sedec/users/${encodeURIComponent(userId)}/edit`, {
    method: "PATCH",
    body: JSON.stringify(data),
  });
}

/**
 * Active/désactive un compte via l'endpoint dédié /status (lecture `active`).
 * Le toggle passe par là plutôt que par updateSedecUser pour la robustesse.
 */
export function setSedecUserStatus(userId: string, active: boolean): Promise<SedecUser> {
  return apiFetch<SedecUser>(`/api/sedec/users/${encodeURIComponent(userId)}/status`, {
    method: "PATCH",
    body: JSON.stringify({ active }),
  });
}

/** Projection minimale {id, denomination} pour les sélecteurs <select> (non paginée). */
export interface SedecEcoleOption {
  id: string;
  denomination: string;
}

export function listSedecEcoles(params?: {
  page?: number; size?: number; q?: string; includeInactifs?: boolean;
}): Promise<Page<SedecEcole>> {
  return apiFetch<Page<SedecEcole>>(
    `/api/sedec/ecoles${qs({
      page: params?.page ?? 0, size: params?.size ?? 20, q: params?.q,
      includeInactifs: params?.includeInactifs ? "true" : undefined,
    })}`,
  );
}

/**
 * Toutes les écoles du diocèse (projection {id, denomination}) pour les <select>
 * de création de compte / filtre école — non paginé, contourne le cap MAX_SIZE
 * de listSedecEcoles.
 */
export function listSedecEcoleOptions(): Promise<SedecEcoleOption[]> {
  return apiFetchOrEmpty<SedecEcoleOption[]>("/api/sedec/ecoles/options", []);
}
export function updateSedecEcole(ecoleId: string, data: Partial<SedecEcole>): Promise<SedecEcole> {
  return apiFetch<SedecEcole>(`/api/sedec/ecoles/${encodeURIComponent(ecoleId)}`, {
    method: "PATCH",
    body: JSON.stringify(data),
  });
}

export function listSedecAudit(params?: {
  page?: number; size?: number; q?: string; from?: string; to?: string;
}): Promise<Page<AuditEntry>> {
  return apiFetch<Page<AuditEntry>>(`/api/sedec/audit${qs({
    page: params?.page ?? 0, size: params?.size ?? 20, q: params?.q, from: params?.from, to: params?.to,
  })}`);
}

// ---------- Diocèse scope (multi-diocese) ----------

export interface DioceseOption { id: string; nom: string; }

/** Diocèses autorisés pour le caller (sélecteur économat). */
export function getMyDioceses(): Promise<DioceseOption[]> {
  return apiFetchOrEmpty<DioceseOption[]>("/api/economat/dioceses", []);
}

/** Catalogue des diocèses (assignation SEDEC). */
export function getDiocesesCatalog(): Promise<DioceseOption[]> {
  return apiFetchOrEmpty<DioceseOption[]>("/api/sedec/dioceses-catalog", []);
}

/** Rattachements diocèses d'un user (admin SEDEC). */
export function getUserDioceses(userId: string): Promise<{ dioceseIds: string[] }> {
  return apiFetch<{ dioceseIds: string[] }>(`/api/sedec/users/${encodeURIComponent(userId)}/dioceses`);
}

export function setUserDioceses(userId: string, dioceseIds: string[]): Promise<unknown> {
  return apiFetch<unknown>(`/api/sedec/users/${encodeURIComponent(userId)}/dioceses`, {
    method: "PUT",
    body: JSON.stringify({ dioceseIds }),
  });
}

// ---------- Années scolaires (back-office) ----------
export interface AnneeScolaire {
  id: string;
  etablissementId: string;
  libelle: string;
  dateDebut: string;
  dateFin: string;
  statut: string;        // A_VENIR | EN_COURS | CLOTUREE (uppercase)
  estActive: boolean;
}
export interface AnneeScolaireCreate {
  etablissementId: string;
  libelle: string;       // "AAAA-AAAA"
  dateDebut: string;     // ISO
  dateFin: string;       // ISO
  estActive?: boolean;
}
export function listAnneesScolaires(etablissementId?: string): Promise<AnneeScolaire[]> {
  const qs = etablissementId ? `?etablissementId=${encodeURIComponent(etablissementId)}` : "";
  return apiFetchOrEmpty<AnneeScolaire[]>(`/api/annees-scolaires${qs}`, []);
}
export function createAnneeScolaire(data: AnneeScolaireCreate): Promise<AnneeScolaire> {
  return apiFetch<AnneeScolaire>("/api/annees-scolaires", { method: "POST", body: JSON.stringify(data) });
}
export function activerAnneeScolaire(id: string): Promise<AnneeScolaire> {
  return apiFetch<AnneeScolaire>(`/api/annees-scolaires/${id}/activate`, { method: "POST" });
}
export function cloturerAnneeScolaire(id: string): Promise<AnneeScolaire> {
  return apiFetch<AnneeScolaire>(`/api/annees-scolaires/${id}/cloturer`, { method: "POST" });
}

// ---------- Années GLOBALES + affectation (SEDEC) ----------
export interface Annee {
  id: string;
  libelle: string;
  dateDebut: string;
  dateFin: string;
  statut: string;          // A_VENIR | EN_COURS | CLOTUREE
  estActive: boolean;
  nbEcoles: number;
}
export interface AnneeCreate {
  libelle: string;
  dateDebut: string;
  dateFin: string;
}
export interface EcoleAffectation {
  ecoleId: string;
  denomination: string;
  codeEtablissementSedec?: string | null;
  communeNom?: string | null;
  membershipId?: string | null;
}
export interface RetraitResult {
  retirees: string[];
  bloquees: { ecoleId: string; denomination: string; raison: string }[];
}

export function listAnnees(params?: {
  q?: string; statut?: string; actifOnly?: boolean; page?: number; size?: number;
}): Promise<Page<Annee>> {
  return apiFetch<Page<Annee>>(`/api/annees${qs({
    q: params?.q, statut: params?.statut,
    actifOnly: params?.actifOnly ? "true" : undefined,
    page: params?.page ?? 0, size: params?.size ?? 20,
  })}`);
}
export function createAnnee(data: AnneeCreate): Promise<Annee> {
  return apiFetch<Annee>("/api/annees", { method: "POST", body: JSON.stringify(data) });
}
export function updateAnnee(id: string, data: AnneeCreate): Promise<Annee> {
  return apiFetch<Annee>(`/api/annees/${encodeURIComponent(id)}`, { method: "PATCH", body: JSON.stringify(data) });
}
export function activerAnnee(id: string): Promise<Annee> {
  return apiFetch<Annee>(`/api/annees/${encodeURIComponent(id)}/activate`, { method: "POST" });
}
export function cloturerAnnee(id: string): Promise<Annee> {
  return apiFetch<Annee>(`/api/annees/${encodeURIComponent(id)}/cloturer`, { method: "POST" });
}
/** Remet « À venir » une année future clôturée par erreur, sans l'activer (22/10/2026). */
export function rouvrirAnnee(id: string): Promise<Annee> {
  return apiFetch<Annee>(`/api/annees/${encodeURIComponent(id)}/rouvrir`, { method: "POST" });
}
export function deleteAnnee(id: string): Promise<void> {
  return apiFetch<void>(`/api/annees/${encodeURIComponent(id)}`, { method: "DELETE" });
}
export function getAffectation(
  anneeId: string,
  params: { cote: "affectees" | "disponibles"; q?: string; page?: number; size?: number },
): Promise<Page<EcoleAffectation>> {
  return apiFetch<Page<EcoleAffectation>>(
    `/api/annees/${encodeURIComponent(anneeId)}/affectation${qs({
      cote: params.cote, q: params.q, page: params.page ?? 0, size: params.size ?? 20,
    })}`,
  );
}
export function affecterEcoles(anneeId: string, ecoleIds: string[]): Promise<{ affectees: number }> {
  return apiFetch<{ affectees: number }>(`/api/annees/${encodeURIComponent(anneeId)}/ecoles`, {
    method: "POST", body: JSON.stringify({ ecoleIds }),
  });
}
export function retirerEcoles(anneeId: string, ecoleIds: string[]): Promise<RetraitResult> {
  return apiFetch<RetraitResult>(`/api/annees/${encodeURIComponent(anneeId)}/ecoles`, {
    method: "DELETE", body: JSON.stringify({ ecoleIds }),
  });
}
/** Affecte TOUTES les écoles filtrées du périmètre (au-delà de la page courante). */
export function affecterTousEcoles(anneeId: string, q?: string): Promise<{ affectees: number }> {
  return apiFetch<{ affectees: number }>(
    `/api/annees/${encodeURIComponent(anneeId)}/ecoles/all${qs({ q })}`,
    { method: "POST" },
  );
}
/** Retire TOUTES les écoles filtrées du périmètre (même garde que le retrait unitaire). */
export function retirerTousEcoles(anneeId: string, q?: string): Promise<RetraitResult> {
  return apiFetch<RetraitResult>(
    `/api/annees/${encodeURIComponent(anneeId)}/ecoles/all${qs({ q })}`,
    { method: "DELETE" },
  );
}

// ---------- Recherche publique eleves ----------

/**
 * Élève choisi sur /payer. La recherche publique (21/10/2026, audit SCOM-01)
 * ne trouve un élève que par son matricule EXACT et ne renvoie que le
 * matricule, le prénom, l'initiale du nom (« K. ») et la classe : ni
 * identifiant, ni sexe, ni date de naissance. `id` et `dateNaissance` ne
 * viennent que d'un enfant lié au compte du parent connecté.
 */
export interface EleveSearchRow {
  id?: string;
  nom: string;
  prenoms: string;
  matricule: string;
  classe?: string | null;
  dateNaissance?: string | null;
}

export function searchElevesPublic(ecoleId: string, q: string): Promise<EleveSearchRow[]> {
  return apiFetch<EleveSearchRow[]>(
    `/api/ecoles/${encodeURIComponent(ecoleId)}/eleves/search?q=${encodeURIComponent(q)}`,
  );
}

// ---------- Import CSV ----------

export interface ImportResponse {
  total: number;
  created: number;
  skipped: number;
  errors: { line: number; message: string }[];
}

export function importElevesCsv(csvBody: string, dryRun?: boolean): Promise<ImportResponse> {
  return uploadCsv("/api/ecole/eleves/import", csvBody, { dryRun });
}

export function importMotifsCsv(
  csvBody: string,
  dioceseId?: string,
  dryRun?: boolean,
): Promise<ImportResponse> {
  return uploadCsv("/api/ecole/motifs/import", csvBody, { dioceseId, dryRun });
}

/**
 * Import des TARIFS PAR CLASSE — format des fiches SEDEC (onglet 04_Baremes) :
 * `classe,codeMotif,montant[,devise]`. Un libellé de niveau (CP, CM1, 6e…)
 * s'applique à toutes les classes du niveau côté backend.
 */
export function importBaremesCsv(csvBody: string, dryRun?: boolean): Promise<ImportResponse> {
  return uploadCsv("/api/ecole/baremes/import", csvBody, { dryRun });
}

/**
 * Import (école/SEDEC) en multipart : le WAF bloque le Content-Type
 * `text/csv` en écriture, on envoie donc un upload de fichier (part `file`).
 * apiFetch ne pose pas de Content-Type quand le body est un FormData → le
 * navigateur ajoute le boundary multipart lui-même.
 *
 * `dryRun` (Lot E) → `?dryRun=true` : le backend parse/valide sans persister
 * (aperçu avant import). `dioceseId` reste optionnel (motifs SEDEC).
 */
function uploadCsv(
  path: string,
  csvBody: string,
  opts: { dioceseId?: string; dryRun?: boolean } = {},
): Promise<ImportResponse> {
  const url = `${path}${qs({
    dioceseId: opts.dioceseId,
    dryRun: opts.dryRun ? "true" : undefined,
  })}`;
  const fd = new FormData();
  fd.append("file", new Blob([csvBody], { type: "text/csv" }), "import.csv");
  return apiFetch<ImportResponse>(url, { method: "POST", body: fd });
}
export function importUsersCsv(csvBody: string, dryRun?: boolean): Promise<ImportResponse> {
  return uploadCsv("/api/sedec/users/import", csvBody, { dryRun });
}
export function importEcolesCsv(csvBody: string, dryRun?: boolean): Promise<ImportResponse> {
  return uploadCsv("/api/sedec/ecoles/import", csvBody, { dryRun });
}

// ---------- Paiements hors système (virement / dépôt banque) ----------

export type PaiementHorsSystemeStatut =
  | "EN_ATTENTE_VALIDATION"
  | "VALIDE"
  | "REJETE";

export interface PaiementHorsSysteme {
  id: string;
  reference: string;
  montantXof: number;
  statut: PaiementHorsSystemeStatut;
  motifId: string | null;
  motifLibelle: string | null;
  eleveId: string | null;
  eleveNom: string | null;
  elevePrenoms: string | null;
  eleveMatricule: string | null;
  payerNom: string | null;
  payerPrenoms: string | null;
  payerEmail: string | null;
  payerTelephone: string | null;
  canal: string;
  banque: string | null;
  referenceExterne: string | null;
  datePaiement: string | null;
  preuveContentType: string;
  preuveUrl: string;
  creeLe: string | null;
  valideLe: string | null;
  motifRejet: string | null;
  /** Tranche déclarée (SCOM-04) ; `null` : règlement du motif. */
  motifEcheanceId?: string | null;
  trancheLibelle?: string | null;
  /** Reste dû actuel du motif, pour une déclaration en attente seulement. */
  resteMotifXof?: number | null;
  /** Reste dû actuel de la tranche déclarée, en attente seulement. */
  resteTrancheXof?: number | null;
}

export interface SoumissionHorsSystemeResult {
  id: string;
  reference: string;
  statut: string;
}

/** Soumission parent (multipart : champs + fichier preuve). Endpoint public. */
export async function soumettrePaiementHorsSysteme(
  form: FormData,
): Promise<SoumissionHorsSystemeResult> {
  return apiFetch<SoumissionHorsSystemeResult>("/api/paiements-hors-systeme", {
    method: "POST",
    body: form,
  });
}

export async function listPaiementsHorsSysteme(
  statut?: string,
): Promise<PaiementHorsSysteme[]> {
  const qs = statut ? `?statut=${encodeURIComponent(statut)}` : "";
  return apiFetch<PaiementHorsSysteme[]>(
    `/api/ecole/paiements-hors-systeme${qs}`,
  );
}

export async function validerPaiementHorsSysteme(
  id: string,
): Promise<PaiementHorsSysteme> {
  return apiFetch<PaiementHorsSysteme>(
    `/api/ecole/paiements-hors-systeme/${id}/valider`,
    { method: "POST" },
  );
}

export async function rejeterPaiementHorsSysteme(
  id: string,
  motif: string,
): Promise<PaiementHorsSysteme> {
  return apiFetch<PaiementHorsSysteme>(
    `/api/ecole/paiements-hors-systeme/${id}/rejeter`,
    { method: "POST", body: JSON.stringify({ motif }) },
  );
}

/**
 * Récupère la preuve (endpoint authentifié econome) en blob → object URL pour
 * un <img>/<iframe> : le navigateur n'enverrait pas le JWT sur un src direct.
 */
export async function getPreuveObjectUrl(id: string): Promise<string> {
  const token = getToken();
  const res = await fetch(
    `${BASE}/api/ecole/paiements-hors-systeme/${id}/preuve`,
    { headers: token ? { Authorization: `Bearer ${token}` } : {} },
  );
  if (!res.ok) {
    throw new ApiError(res.status, "Preuve indisponible", null);
  }
  const blob = await res.blob();
  return URL.createObjectURL(blob);
}

// =====================================================================
// Reçu PDF — aperçu inline (object URL) sans téléchargement
//
// Deux variantes selon l'appelant :
//   - back-office / parent authentifié : /api/paiements/{id}/recu.pdf (Bearer)
//   - lien public invité : /api/paiements-public/{token}/recu.pdf (sans auth)
// Le navigateur n'enverrait pas le JWT sur un `src` direct → on récupère le
// blob nous-mêmes et on renvoie un object URL injectable dans une <iframe>.
// =====================================================================

/**
 * Reçu d'un paiement (endpoint authentifié) en blob → object URL.
 * Miroir de getPreuveObjectUrl : même endpoint que downloadRecuPdf
 * (/api/paiements/{id}/recu.pdf) mais sans déclencher le téléchargement.
 */
export async function getRecuObjectUrl(paiementId: string): Promise<string> {
  const token = getToken();
  const res = await fetch(
    `${BASE}/api/paiements/${encodeURIComponent(paiementId)}/recu.pdf`,
    { headers: token ? { Authorization: `Bearer ${token}` } : {} },
  );
  if (!res.ok) {
    throw new ApiError(res.status, "Reçu indisponible", null);
  }
  const blob = await res.blob();
  return URL.createObjectURL(blob);
}

/**
 * Reçu public (lien invité) en blob → object URL. Le backend peut répondre
 * 425 Too Early / 503 tant que le PDF est en cours de génération : on retente
 * avec un backoff (même logique que DownloadRecuCard) avant d'abandonner.
 */
export async function getRecuObjectUrlByToken(token: string): Promise<string> {
  const url = `${BASE}/api/paiements-public/${encodeURIComponent(token)}/recu.pdf`;
  const maxAttempts = 6;
  let attempt = 0;
  let lastStatus = 0;
  while (attempt < maxAttempts) {
    const res = await fetch(url);
    if (res.ok) {
      const blob = await res.blob();
      return URL.createObjectURL(blob);
    }
    if (res.status === 425 || res.status === 503) {
      attempt += 1;
      lastStatus = res.status;
      await new Promise((r) => setTimeout(r, 1500 * attempt));
      continue;
    }
    throw new ApiError(res.status, "Reçu indisponible", null);
  }
  throw new ApiError(lastStatus, "Reçu pas encore prêt", null);
}

// ---------------------------------------------------------------------------
// Fiches parents — correction par l'école (2026-08-14)
// ---------------------------------------------------------------------------

/**
 * Fiche parent telle que renvoyée par `GET /api/parents` (school-service).
 * Le couple nom/prénoms est celui qui figure comme PAYEUR sur les reçus : une
 * fiche créée automatiquement pouvait porter un nom déduit de l'email, sans
 * personne pour la corriger.
 */
export interface ParentFiche {
  id: string;
  nom: string;
  prenoms: string;
  telephone: string;
  email?: string | null;
  profession?: string | null;
  adresse?: string | null;
  userId?: string | null;
}

/** Champs corrigeables. `userId` n'est JAMAIS envoyé : le lien au compte du
 *  parent ne se modifie pas depuis un formulaire de coordonnées. */
export interface ParentFichePatch {
  nom: string;
  prenoms: string;
  telephone: string;
  email?: string;
  profession?: string;
  adresse?: string;
}

/**
 * Parents de MON école. Aucun `etablissementId` n'est transmis : le serveur
 * confine un compte d'école à son établissement, et lui en demander un autre
 * est refusé (403). `search` filtre nom / prénoms / téléphone / email.
 */
export function listParents(search?: string): Promise<ParentFiche[]> {
  const qs = search && search.trim() ? `?search=${encodeURIComponent(search.trim())}` : "";
  return apiFetchOrEmpty<ParentFiche[]>(`/api/parents${qs}`, []);
}

/** Corrige une fiche parent (nom, prénoms, coordonnées). */
export function updateParent(id: string, body: ParentFichePatch): Promise<ParentFiche> {
  return apiFetch<ParentFiche>(`/api/parents/${encodeURIComponent(id)}`, {
    method: "PATCH",
    body: JSON.stringify(body),
  });
}

// ---------------------------------------------------------------------------
// Vitrine des établissements (29/08) — le SEDEC saisit pour TOUT le réseau
// ---------------------------------------------------------------------------
// L'école renseigne la sienne depuis CATHO SCHOOL PRO ; le SEDEC dispose ici de
// la même saisie pour n'importe quel établissement. Le serveur confine les
// rôles d'école, quelle que soit l'adresse appelée — ce module ne transporte.

export interface RubriqueVitrine {
  texte?: string | null;
  photos?: string[] | null;
}

export interface Vitrine {
  photoUrl?: string | null;
  rubriques?: Record<string, RubriqueVitrine> | null;
  remplies?: number;
}

/** Les six rubriques, dans leur ordre d'affichage. */
export const RUBRIQUES_VITRINE: { cle: string; titre: string; aide: string }[] = [
  { cle: "presentation", titre: "Présentation", aide: "Mot de la direction, histoire, projet éducatif." },
  { cle: "vie-scolaire", titre: "Vie scolaire", aide: "Cantine, transport, internat, horaires." },
  { cle: "pedagogie", titre: "Pédagogie", aide: "Cycles, filières, langues, résultats." },
  { cle: "pastorale", titre: "Pastorale", aide: "Aumônerie, sacrements, temps forts." },
  { cle: "admissions", titre: "Admissions", aide: "Périodes, pièces à fournir, frais indicatifs." },
  { cle: "contacts", titre: "Contacts & accès", aide: "Téléphones, adresse, repères pour venir." },
];

export const VITRINE_TEXTE_MAX = 1200;

export async function getVitrine(etablissementId: string): Promise<Vitrine> {
  return apiFetch<Vitrine>(`/api/etablissements/vitrine/${encodeURIComponent(etablissementId)}`);
}

export async function putVitrine(
  etablissementId: string,
  rubriques: Record<string, RubriqueVitrine>,
): Promise<Vitrine> {
  return apiFetch<Vitrine>(`/api/etablissements/vitrine/${encodeURIComponent(etablissementId)}`, {
    method: "PUT",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(rubriques),
  });
}

export async function uploadVitrinePhoto(
  etablissementId: string,
  fichier: File,
): Promise<{ photoUrl: string }> {
  const form = new FormData();
  // Le champ s'appelle « photo » côté serveur : un autre nom récolte un 400.
  form.append("photo", fichier);
  return apiFetch<{ photoUrl: string }>(
    `/api/etablissements/vitrine/${encodeURIComponent(etablissementId)}/photo`,
    { method: "POST", body: form },
  );
}

/* ------------------------------------------------------------------ */
/* Comptes de reversement — où l'argent encaissé est renvoyé à l'école */
/* ------------------------------------------------------------------ */

export interface CompteReversement {
  id: string;
  etablissementId: string;
  libelle: string;
  type: "BANQUE" | "MOBILE_MONEY";
  banque?: string | null;
  /** Masqué (« •••• 1234 ») sauf lecture explicite `complet: true`. */
  numeroCompte: string;
  titulaire?: string | null;
  motifId?: string | null;
  parDefaut: boolean;
  actif: boolean;
  verifie: boolean;
  verifieLe?: string | null;
  source?: string | null;
  note?: string | null;
  creeLe: string;
  modifieLe: string;
}

export interface CompteReversementInput {
  libelle: string;
  type?: "BANQUE" | "MOBILE_MONEY";
  banque?: string;
  numeroCompte: string;
  titulaire?: string;
  motifId?: string | null;
  parDefaut?: boolean;
  actif?: boolean;
  source?: string;
  note?: string;
}

/** Réponse de création : la fiche + les écoles qui déclarent déjà ce numéro. */
export interface CompteReversementCree {
  compte: CompteReversement;
  autresEcolesMemeNumero: string[];
}

export function listComptesReversement(complet?: boolean): Promise<CompteReversement[]> {
  return apiFetchOrEmpty<CompteReversement[]>(
    `/api/ecole/comptes-reversement${qs({ complet: complet ? "true" : undefined })}`, []);
}

export function createCompteReversement(data: CompteReversementInput): Promise<CompteReversementCree> {
  return apiFetch<CompteReversementCree>("/api/ecole/comptes-reversement", {
    method: "POST",
    body: JSON.stringify(data),
  });
}

export function updateCompteReversement(id: string, data: CompteReversementInput): Promise<CompteReversement> {
  return apiFetch<CompteReversement>(`/api/ecole/comptes-reversement/${encodeURIComponent(id)}`, {
    method: "PUT",
    body: JSON.stringify(data),
  });
}

/** Confirmation par l'école que le numéro est bien le sien. */
export function verifierCompteReversement(id: string): Promise<CompteReversement> {
  return apiFetch<CompteReversement>(
    `/api/ecole/comptes-reversement/${encodeURIComponent(id)}/verifier`, { method: "POST" });
}

export function desactiverCompteReversement(id: string): Promise<void> {
  return apiFetch<void>(`/api/ecole/comptes-reversement/${encodeURIComponent(id)}`, { method: "DELETE" });
}

/** Vue SEDEC : une ligne par école, numéros masqués. */
export interface CompteReversementReseau {
  etablissementId: string;
  ecole: string;
  libelle?: string | null;
  type?: string | null;
  banque?: string | null;
  numeroMasque?: string | null;
  titulaire?: string | null;
  verifie: boolean;
  partageAvecUneAutreEcole: boolean;
  etat: "AUCUN_COMPTE" | "A_CONFIRMER" | "CONFIRME" | "NUMERO_PARTAGE_AVEC_UNE_AUTRE_ECOLE";
}

export function listComptesReversementReseau(): Promise<CompteReversementReseau[]> {
  return apiFetchOrEmpty<CompteReversementReseau[]>("/api/sedec/comptes-reversement", []);
}

// ─── Diffusion : SMS des annonces aux familles (SEDEC) ──────────────────────

/**
 * État des SMS d'annonces aux familles (22/10/2026, décision du PO) :
 * `plafondEnvironnement` = la configuration du serveur le permet (sinon rien
 * ne part, quoi que dise le réglage), `actifReseau` = réglage du réseau,
 * `effectif` = des SMS partent réellement.
 */
export interface ReglageSmsAnnonces {
  plafondEnvironnement: boolean;
  actifReseau: boolean;
  effectif: boolean;
  modifieLe?: string | null;
  modifiePar?: string | null;
  ecoles: {
    etablissementId: string;
    nom?: string | null;
    actif: boolean;
    modifieLe?: string | null;
    modifiePar?: string | null;
  }[];
}

export function getReglageSmsAnnonces(): Promise<ReglageSmsAnnonces> {
  return apiFetch<ReglageSmsAnnonces>("/api/sedec/diffusion/sms-annonces");
}

/** Allume ou coupe les SMS d'annonces de tout le réseau (journalisé). */
export function basculerSmsAnnonces(actif: boolean): Promise<ReglageSmsAnnonces> {
  return apiFetch<ReglageSmsAnnonces>("/api/sedec/diffusion/sms-annonces", {
    method: "PUT",
    body: JSON.stringify({ actif }),
  });
}

/** Rend une école au réglage du réseau (`actif` true) ou la coupe (false). */
export function basculerSmsAnnoncesEcole(etablissementId: string, actif: boolean): Promise<ReglageSmsAnnonces> {
  return apiFetch<ReglageSmsAnnonces>(
    `/api/sedec/diffusion/sms-annonces/ecoles/${encodeURIComponent(etablissementId)}`,
    { method: "PUT", body: JSON.stringify({ actif }) },
  );
}

// ─── Suivi des écoles (SEDEC) ───────────────────────────────────────────────

/** Feu rouge calculé par le serveur : code stable, libellé en français. */
export interface FeuRougeEcole {
  code: string;
  libelle: string;
}

/**
 * Une école active de l'année active, vue par le SEDEC. Aucune donnée
 * personnelle : des codes, un nom d'école et des volumes ; montants en F CFA.
 */
export interface SuiviEcole {
  etablissementId: string;
  code: string | null;
  nom: string | null;
  direction: number;
  econome: number;
  caisse: number;
  secretariatEtudes: number;
  enseignants: number;
  derniereConnexion: string | null;
  inscrits: number;
  sansClasse: number;
  parents: number;
  motifs: number;
  echeances: number;
  reversementConfirme: boolean;
  orange: boolean;
  jeko: boolean;
  encaisse7j: number;
  encaisseAnnee: number;
  duAnnee: number;
  declaresEnAttente: number;
  declaresEnAttentePlus48h: number;
  enLigneBloques: number;
  notes7j: number;
  appels7j: number;
  bulletinsPublies: number;
  feuxRouges: FeuRougeEcole[];
}

export interface SuiviEcoles {
  /** Libellé de l'année active, null si aucune n'est active. */
  annee: string | null;
  calculeLe: string | null;
  ecoles: SuiviEcole[];
}

/**
 * Suivi des écoles du diocèse courant (en-tête X-Diocese-Id). Pas de repli
 * silencieux : une erreur doit se voir, sinon une panne passerait pour un
 * réseau sans alerte.
 */
export function getSuiviEcoles(): Promise<SuiviEcoles> {
  return apiFetch<SuiviEcoles>("/api/sedec/suivi-ecoles");
}

// ─── Premier compte d'une école (SEDEC) ─────────────────────────────────────
export interface CompteEcoleInput {
  email: string;
  firstName: string;
  lastName: string;
  /** Rôle délégable : direction, econome, secretaire… (cf. auth-service). */
  role: string;
}
export interface CompteEcoleResult {
  userId: string;
  /** false : le compte existait, le rôle lui a été ajouté. */
  created: boolean;
  role: string;
  ecoleId: string;
}
/**
 * Ouvre (ou complète) le compte d'une personne d'une école depuis le SEDEC.
 * Le SEDEC est un appelant réseau : l'école est donnée explicitement. Sans
 * cet écran, seul un super administrateur pouvait ouvrir le premier compte
 * d'une école nouvellement créée (recette du 15/10/2026).
 */
export function ouvrirCompteEcole(ecoleId: string, input: CompteEcoleInput): Promise<CompteEcoleResult> {
  return apiFetch<CompteEcoleResult>("/api/auth/school-staff/invitations", {
    method: "POST",
    body: JSON.stringify({ ...input, ecoleId }),
  });
}
